import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { useAuth } from '@shared/state/hooks/useAuth';
import { UserRole } from '@shared/types';
import { phoneNumberStringFormator } from '@shared/utils/phoneNumberStringFormator';
import { useSearchParams } from 'react-router-dom';

import { GiftCallHelperBox } from './GiftCallHelperBox';

const noop = () => undefined;

interface GiftAuthGateContextValue {
  requestReauth: () => void;
}

const GiftAuthGateContext = createContext<GiftAuthGateContextValue>({
  requestReauth: noop,
});

export const useGiftAuthGate = () => useContext(GiftAuthGateContext);

interface GiftStoreAuthGateProps {
  children: ReactNode;
}

const GiftStoreAuthGate: FC<GiftStoreAuthGateProps> = ({ children }) => {
  const { userProfile, signupOrLoginStoreGuest } = useAuth();

  const [reauthRequested, setReauthRequested] = useState(false);

  const hasValidRole =
    !!userProfile &&
    userProfile.roles?.some(
      (r: string) =>
        r === UserRole.StoreGuest || r === UserRole.StoreCustomer || r === UserRole.Admin,
    );

  const showLoginForm = !hasValidRole || reauthRequested;

  const [searchParams] = useSearchParams();
  const autoLoginAttempted = useRef(false);

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!showLoginForm || autoLoginAttempted.current) return;

    const secretKey = searchParams.get('secretKey');
    const qName = searchParams.get('name');
    const qPhone = searchParams.get('phoneNumber');

    if (
      !secretKey ||
      !qName ||
      !qPhone ||
      secretKey !== process.env.REACT_APP_GIFT_STORE_ADMIN_PREVIEW_KEY
    )
      return;

    autoLoginAttempted.current = true;
    const cleanPhone = qPhone.replace(/[^0-9]/g, '');

    signupOrLoginStoreGuest(
      {
        // 결제 시 계정 생성과 동일하게 이름의 모든 공백 제거 (앞뒤+중간)
        name: qName.replace(/\s/g, ''),
        phoneNumber: cleanPhone,
        password: cleanPhone.slice(-4),
      },
      true,
    )
      .then(() => setReauthRequested(false))
      .catch(() => {
        setName(qName);
        setPhone(cleanPhone);
        setError('자동 로그인에 실패했습니다. 직접 입력해주세요.');
      });
  }, [showLoginForm, searchParams, signupOrLoginStoreGuest]);

  const requestReauth = useCallback(() => {
    setReauthRequested(true);
    setError('');
  }, []);

  const handleLogin = useCallback(async () => {
    if (!name.trim()) {
      setError('주문자 성함을 입력해주세요.');
      return;
    }
    if (!phone.trim() || phone.replace(/[^0-9]/g, '').length < 10) {
      setError('올바른 휴대전화번호를 입력해주세요.');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const cleanPhone = phone.replace(/[^0-9]/g, '');
      await signupOrLoginStoreGuest(
        {
          // 결제 시 계정 생성과 동일하게 이름의 모든 공백 제거 (앞뒤+중간)
          name: name.replace(/\s/g, ''),
          phoneNumber: cleanPhone,
          password: cleanPhone.slice(-4),
        },
        true,
      );
      setReauthRequested(false);
    } catch {
      setError('로그인에 실패했습니다. 주문 시 입력한 정보와 동일한지 확인해주세요.');
    } finally {
      setIsLoading(false);
    }
  }, [name, phone, signupOrLoginStoreGuest]);

  const contextValue: GiftAuthGateContextValue = { requestReauth };

  if (!showLoginForm) {
    return (
      <GiftAuthGateContext.Provider value={contextValue}>
        {children}
      </GiftAuthGateContext.Provider>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-gray-50">
      <div className="flex-1 px-4 pt-8">
        <div className="mx-auto max-w-md">
          <div className="text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-indigo-100">
              <svg
                className="h-7 w-7 text-indigo-600"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z"
                />
              </svg>
            </div>
            <h2 className="mt-4 text-xl font-bold text-gray-800">본인 확인</h2>
            <p className="mt-1 text-sm text-gray-500">
              주문 시 입력한 정보를 입력해주세요.
            </p>
          </div>

          <div className="mt-6 rounded-xl bg-white p-5 shadow-md">
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  주문자 성함
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="성함을 입력해주세요"
                  className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  주문자 휴대전화번호
                </label>
                <input
                  type="tel"
                  value={phoneNumberStringFormator(phone)}
                  onChange={(e) => setPhone(e.target.value.replace(/[^0-9]/g, ''))}
                  placeholder="010-0000-0000"
                  maxLength={13}
                  className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
              </div>
            </div>

            {error && <p className="mt-3 text-center text-sm text-red-500">{error}</p>}

            <button
              onClick={handleLogin}
              disabled={isLoading}
              className="mt-4 w-full rounded-lg bg-indigo-600 py-3 text-base font-bold text-white transition active:bg-indigo-700 disabled:bg-gray-300"
            >
              {isLoading ? '확인 중...' : '확인'}
            </button>
          </div>
        </div>
      </div>

      <div className="mt-6">
        <GiftCallHelperBox />
      </div>
    </div>
  );
};

export default GiftStoreAuthGate;
